// stores/useEmailTemplateStore.ts

import { defineStore } from 'pinia';
import { ref, computed, Ref } from 'vue';
import { useQuery, useMutation, useQueryClient, UseQueryReturnType } from '@tanstack/vue-query';
import { BranchDTO, IBranchDTO, IPaginationDTO, PaginationDTO } from '@/dtos';
import {
  createBranchAPI,
  getBranchByIdAPI,
  getBranchesAPI,
  saveBranchApi,
  updateBranchForTeamAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from '../updateStatus';
import { useAuthStore } from '../auth';

export const useBranchStore = defineStore('branch', () => {
  const statusStore = useUpdateStatusStore();
  const authStore = useAuthStore();
  const pagination = ref<IPaginationDTO>(new PaginationDTO());
  const queryClient = useQueryClient();
  const paginationQueryKey = computed(() => ['branches', pagination.value]);

  const getAllBranchesQuery = useQuery({
    queryKey: paginationQueryKey,
    queryFn: async (): Promise<{ data: IBranchDTO[]; count: number }> => {
      const ret = await getBranchesAPI(pagination.value);
      const branches = await Promise.all<IBranchDTO[]>(
        ret.data.map((branch) => new BranchDTO(branch)),
      );
      return { data: branches, count: ret.count };
    },
    enabled: computed(() => !!authStore.isAdmin),
  });

  const getBranchByIdQuery = (branchId: Ref<string>): UseQueryReturnType<IBranchDTO, Error> => {
    return useQuery<IBranchDTO, Error>({
      queryKey: computed(() => ['branch', branchId]),
      queryFn: async () => {
        const branch = await getBranchByIdAPI(branchId.value);
        return new BranchDTO(branch);
      },
    });
  };

  // Mutations
  const create = useMutation({
    mutationFn: (branch: IBranchDTO) => {
      return createBranchAPI(branch);
    },
    onSuccess: () => {
      const key = `branch-create`;
      statusStore.setSuccessStatus(key, 'success:create');
      queryClient.invalidateQueries({ queryKey: ['branches'] });
    },
    onError: (err: string) => {
      const key = `branch-create`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const update = useMutation({
    mutationFn: (branch: IBranchDTO) => {
      return saveBranchApi(branch);
    },
    onSuccess: (data: IBranchDTO) => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch', data.id] });
      const key = `branch-${data.id}`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string, variables) => {
      const key = `branch-${variables.id}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const changeBranchForTeam = useMutation({
    mutationFn: ({
      teamId,
      yearId,
      branchId,
    }: {
      teamId: string;
      yearId: string;
      branchId: string;
    }) => {
      return updateBranchForTeamAPI(teamId, yearId, branchId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId, variables.yearId] });
      const key = `branch-change-${variables.teamId}-${variables.yearId}-${variables.branchId}`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string, variables) => {
      const key = `branch-change-${variables.teamId}-${variables.yearId}-${variables.branchId}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const invalidate = (branchId?: string) => {
    queryClient.invalidateQueries({ queryKey: ['branches'] });
    if (branchId) {
      queryClient.invalidateQueries({ queryKey: ['branch', branchId] });
    }
  };

  return {
    pagination,
    getAllBranchesQuery,
    getBranchByIdQuery,
    changeBranchForTeam,
    create,
    update,
    invalidate,
  };
});
